/// <amd-module name="Release/Helpers/Link" />
import { IVssPageContext } from "VSS/Platform/Context";
import { routeUrl } from "VSS/Platform/Location";
import { FastPageSwitch } from "VSS/Platform/FPS";
import { IVssNavigationService } from "VSS/Platform/Navigation";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { traceHubException } from "Telemetry";

export function createUrlForRoute(
    pageContext: IVssPageContext,
    routeContributionId: string,
    queryParams: { [key: string]: string }
): string {
    const curentRouteValues = pageContext
        .getService<IVssNavigationService>("IVssNavigationService")
        .getCurrentRoute()
        .routeValues;

    let routeValues: { [key: string]: string } = { ...queryParams };
    let isQueryProjectDifferent = queryParams["project"] && queryParams["project"] !== curentRouteValues.project;
    if (!isQueryProjectDifferent) {
        routeValues["project"] = curentRouteValues.project;
        if (curentRouteValues.team) {
            routeValues.team = curentRouteValues.team;
        }
    }

    try {
        const constructedUrl = routeUrl(
            pageContext,
            routeContributionId,
            routeValues);

        return constructedUrl;
    }
    catch (ex) {
        const routes = pageContext.getService<IVssContributionService>("IVssContributionService").getSharedData<object>("_routes");
        const properties = routes ? { ["availableRoutes"]: Object.keys(routes) } : {}
        traceHubException(pageContext, "ResolveRoute", ex, properties);
        return "";
    }
}

export function handleEventAndNavigate(pageContext: IVssPageContext, event: React.SyntheticEvent<HTMLElement> | React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement> | undefined, url: string): void {
    if (!url) {
        return;
    }

    if (event
        && ((event as React.KeyboardEvent<HTMLElement>).ctrlKey
            || ((event as React.MouseEvent<HTMLElement>).nativeEvent
                && (event as React.MouseEvent<HTMLElement>).nativeEvent.which === 2))) {
        const newTab = window.open();
        if (newTab) {
            newTab.opener = null;
            newTab.location.href = url;
        }
    }
    else {
        FastPageSwitch(pageContext, url, true);
    }

    if (event) {
        event.preventDefault();
        event.stopPropagation();
    }
}

/** Returns a link to the release progress page.  */
export function createReleaseViewUrl(pageContext: IVssPageContext, releaseId: number): string {
    return createUrlForRoute(
        pageContext,
        "ms.vss-releaseManagement-web.cd-release-progress-default-route",
        {
            "_a": "release-pipeline-progress",
            "releaseId": releaseId.toString(10)
        }
    );
}

/** Returns a link to the summary page for the environment.  */
export function createReleaseEnvironmentUrl(pageContext: IVssPageContext, definitionEnvironmentId: number, releaseDefinitionId: number): string {
    return createUrlForRoute(
        pageContext,
        "ms.vss-releaseManagement-web.hub-explorer-3-default-route",
        {
            ["definitionId"]: releaseDefinitionId.toString(),
            ["environmentId"]: definitionEnvironmentId.toString(),
            ["_a"]: "deployments"
        }
    );
}

/** Returns a link to the summary page for the release definition.  */
export function createReleaseDefinitionUrl(pageContext: IVssPageContext, releaseDefinitionId: number): string {
    return createUrlForRoute(
        pageContext,
        "ms.vss-releaseManagement-web.hub-explorer-3-default-route",
        {
            ["definitionId"]: releaseDefinitionId.toString(),
            ["_a"]: "releases"
        }
    );
}

/** Returns a link to release logs for the given environment and release. */
export function createReleaseLogsUrl(pageContext: IVssPageContext, releaseId: number, releaseEnvironmentId: number): string {
    return createUrlForRoute(
        pageContext,
        "ms.vss-releaseManagement-web.cd-release-progress-default-route",
        {
            ["releaseId"]: releaseId.toString(),
            ["environmentId"]: releaseEnvironmentId.toString(),
            ["_a"]: "release-environment-logs"
        }
    );
}

/** Returns a link to the test results tab for the release  */
export function createTestResultsTabUrl(pageContext: IVssPageContext, releaseId: number, releaseEnvironmentId: number): string {
    return createUrlForRoute(
        pageContext,
        "ms.vss-releaseManagement-web.cd-release-progress-default-route",
        {
            ["releaseId"]: releaseId.toString(),
            ["environmentId"]: releaseEnvironmentId.toString(),
            ["_a"]: "release-environment-extension",
            ["extensionId"]: "ms.vss-test-web.test-result-in-release-environment-editor-tab"
        }
    );
}

/** Returns a link to the results page for a build  */
export function createBuildResultsUrl(pageContext: IVssPageContext, buildId: number): string {
    return createUrlForRoute(
        pageContext,
        "ms.vss-build-web.ci-results-hub-route",
        {
            ["buildId"]: buildId.toString()
        }
    );
}

/** Returns a link to the build definition page  */
export function createBuildDefinitionUrl(pageContext: IVssPageContext, definitionId: number): string {
    return createUrlForRoute(
        pageContext,
        "ms.vss-build-web.pipelines-hub-route",
        {
            ["definitionId"]: definitionId.toString()
        }
    );
}